/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useRef, useState} from 'react';
import {MenuItem, MenuItems} from '../../../types/components';
import {FLYOUT_SUBMENU_ITEM_THRESHOLD} from '../constants';

/**
 * Defines the inputs required to manage current-navigation submenu behavior.
 */
interface UseNavigationParams {
  activeIndex: number;
  closeSecondaryNavigation?: () => void;
  collapsed: boolean;
  menuItems: MenuItems;
  onSetPage(page: string, index: number, isSecondaryIndex?: boolean, item?: MenuItem, isMainMenu?: boolean): void;
  showSecondaryNavigation?: boolean;
}

/**
 * Manages current-navigation-specific submenu flyout and top-tab state.
 *
 * @param params Defines the current navigation state and callbacks.
 * @returns The flyout state, anchor handlers, and submenu toggle callbacks.
 */
export const useNavigation = ({
  activeIndex,
  collapsed,
  menuItems,
  showSecondaryNavigation
}: UseNavigationParams) => {
  const [submenuAnchor, setSubmenuAnchor]           = useState<HTMLElement | null>(null);
  const [submenuAnchorIndex, setSubmenuAnchorIndex] = useState<number | null>(null);

  const submenuAnchorRefs                 = useRef<Record<number, HTMLElement | null>>({});
  const submenuAnchorIndexRef             = useRef<number | null>(null);
  const activeMenuItem                    = activeIndex >= 0 ? menuItems[activeIndex] : undefined;
  const useTopNavigationForActiveSubmenu  = !!activeMenuItem?.pages?.length && (
    activeMenuItem.pages.length >= FLYOUT_SUBMENU_ITEM_THRESHOLD
  );

  const useFlyoutForActiveSubmenu = (
    collapsed &&
    !!activeMenuItem?.pages?.length &&
    !useTopNavigationForActiveSubmenu
  );

  const closeSubmenuFlyout = useCallback(() => {
    submenuAnchorIndexRef.current = null;
    setSubmenuAnchor(null);
    setSubmenuAnchorIndex(null);
  }, []);

  const handleSubmenuToggle = useCallback((anchorEl: HTMLElement | null, index: number) => {
    if (anchorEl && submenuAnchorIndexRef.current === index) {
      return;
    }
    submenuAnchorIndexRef.current = anchorEl ? index : null;
    setSubmenuAnchor(anchorEl);
    setSubmenuAnchorIndex(anchorEl ? index : null);
  }, []);

  const registerSubmenuAnchor = useCallback((index: number, element: HTMLElement | null) => {
    submenuAnchorRefs.current[index] = element;
  }, []);

  useEffect(() => {
    if (!collapsed) {
      submenuAnchorIndexRef.current = null;
      setSubmenuAnchor(null);
      setSubmenuAnchorIndex(null);
    }
  }, [collapsed]);

  useEffect(() => {
    if (collapsed || !useFlyoutForActiveSubmenu || !showSecondaryNavigation || submenuAnchor) {
      return;
    }
    const activeAnchor = submenuAnchorRefs.current[activeIndex];
    if (activeAnchor) {
      submenuAnchorIndexRef.current = activeIndex;
      setSubmenuAnchor(activeAnchor);
      setSubmenuAnchorIndex(activeIndex);
    }
  }, [activeIndex, collapsed, showSecondaryNavigation, submenuAnchor, useFlyoutForActiveSubmenu]);

  useEffect(() => {
    if (!collapsed || !submenuAnchor) {
      return undefined;
    }
    const handleMouseMove = (event: MouseEvent) => {
      const flyout       = document.querySelector('[data-navigation-flyout="true"]');
      const flyoutBounds = flyout?.getBoundingClientRect();
      const anchorBounds = submenuAnchor.getBoundingClientRect();
      const isInFlyout   = !!flyoutBounds &&
        event.clientX >= flyoutBounds.left &&
        event.clientX <= flyoutBounds.right &&
        event.clientY >= flyoutBounds.top &&
        event.clientY <= flyoutBounds.bottom;
      const isInFlyoutBridge = !!flyoutBounds &&
        event.clientX >= anchorBounds.right &&
        event.clientX <= flyoutBounds.left &&
        event.clientY >= Math.min(anchorBounds.top, flyoutBounds.top) &&
        event.clientY <= Math.max(anchorBounds.bottom, flyoutBounds.bottom);
      const isInNavigationRail = event.clientX <= anchorBounds.right;
      if (!isInFlyout && !isInFlyoutBridge && !isInNavigationRail) {
        closeSubmenuFlyout();
      }
    };
    document.addEventListener('mousemove', handleMouseMove);
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, [collapsed, closeSubmenuFlyout, submenuAnchor]);

  return {
    closeSubmenuFlyout,
    handleSubmenuToggle,
    registerSubmenuAnchor,
    submenuAnchor,
    submenuAnchorIndex,
    useFlyoutForActiveSubmenu,
    useTopNavigationForActiveSubmenu
  };
};

export default useNavigation;
